import { Overlay, OverlayConfig } from "@angular/cdk/overlay";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { Subject } from "rxjs";
import { Canvas, CanvasOptions, Ellipse, FabricImage, FabricText, filters, Line, Path, PencilBrush, Point, Rect } from "fabric";
import { InspectionImage } from "../../../_objects/inspection-image";
import { InspectionImageService } from "../../../_services/http-api/inspection-image.service";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { AddTextComponent } from "../add-text/add-text.component";
import { ANNOTATION_COLOR_ITEMS, MARKCOLORITEMS, USE_IMAGE_TYPE } from "../../../app.constants";
import { CommonConfirmDialogComponent } from "../../../common-dialog/common-confirm-dialog.component";
import { takeUntil } from "rxjs/operators";
import { ImageTrimComponent } from "../../../_shared/component/image-trim/image-trim.component";
import { SpinnerService } from "../../../_services/spinner.service";
import { TranslateService } from "@ngx-translate/core";
import { LangUpdateService } from "../../../_services/lang-update.service";
import { INSPECTION_IMAGE__RETOUCH_SETTING_QUERY_PARAMS, RetouchSettingsQueryParam, StorageService } from "../../../_services/storage.service";
import { BlobService } from "../../../_services/blob.service";
import { IconEnum } from "@evc/web-components";

export class AnnotationEmitData {
  modifiedImage: Blob;
  annotationObjects: any;
  measurementObjects: any;
  initDataJson: any;
  isDisable: boolean;

  constructor(modifiedImage: Blob, annotationObjects: any, measurementObjects: any, initDataJson: any, isDisable: boolean) {
    this.modifiedImage = modifiedImage;
    this.annotationObjects = annotationObjects;
    this.measurementObjects = measurementObjects;
    this.initDataJson = initDataJson;
    this.isDisable = isDisable;
  }
}

@Component({
  selector: "app-image-annotation-edit",
  templateUrl: "./image-annotation-edit.component.html",
  styleUrls: ["./image-annotation-edit.component.scss"],
  standalone: false
})
export class ImageAnnotationEditComponent implements OnInit, OnDestroy, OnChanges {
  @Input() inspectionImage: InspectionImage;
  @Input() annotationObjects: any = null;
  @Input() isDataLoaded = false;
  @Input() isEditable: boolean;
  @Output() modified = new EventEmitter<AnnotationEmitData>();

  squareIcon = IconEnum.Square;
  readonly TRIM_HEIGHT = 800;
  readonly TRIM_WIDTH = 800;
  readonly DUMMY = "dummy";
  readonly MAIN = "main";
  readonly MAX_HISTORIES = 1000;
  readonly borderTypeList: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 30, 50, 100];
  readonly fillPercentageList = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  readonly opacityColorCodes = new Map<number, string>([
    [0, "00"],
    [10, "1A"],
    [20, "33"],
    [30, "4D"],
    [40, "66"],
    [50, "80"],
    [60, "99"],
    [70, "B3"],
    [80, "CC"],
    [90, "E6"],
    [100, "FF"]
  ]);
  readonly selectColors: MARKCOLORITEMS[] = ANNOTATION_COLOR_ITEMS;
  readonly editFunctions = [this.translate.instant("imageAnnotationEdit.annotation"), this.translate.instant("imageAnnotationEdit.imageRetouch")];
  readonly maxBrightness = 1;
  readonly minBrightness = -1;

  public canvas: Canvas;
  public cropCanvas: Canvas;
  public imageSrc: string;
  public cropImageSrc: string;
  public imgSrcBase64: string;
  private rectangle = new Rect({
    fill: "transparent",
    stroke: "#ccc",
    strokeDashArray: [2, 2],
    visible: false
  });
  public histories = [];
  public activeHistoryIndex = -1;
  public fillOpacity = 0;
  public fillColor = "#ffffff";
  public isFilled = false;
  public borderType = 6;
  public borderColor = "#000000";
  public initLoaded = false;
  public initDataJson: string;
  public selectFunction: string;
  public brightness = 0.0;
  public isBorderEditable = true;
  public isCanvasLoading = true;
  public scale = 1;
  public baseImageWidth: number;
  public baseImageHeight: number;
  public baseImage: HTMLImageElement;
  public resizeTimer = null;
  public mainContent: HTMLElement;

  private firstActiveObjIndex = -1;
  private baseMainImage: FabricImage;
  private canvasImage: FabricImage;
  private onDestroy$ = new Subject();
  private canvasEventDisposers: VoidFunction[] = [];

  get RETOUCH_SETTINGS_QUERY_PARAM(): RetouchSettingsQueryParam {
    return {
      fill: {
        isFill: this.isFilled,
        color: this.fillColor
      },
      border: {
        strokeWidth: this.borderType,
        color: this.borderColor
      }
    };
  }

  get isAnnotationFunc() {
    return this.selectFunction === this.editFunctions[0];
  }

  @ViewChild("GenImag") imgEl: ElementRef;

  constructor(
    private cd: ChangeDetectorRef,
    private overlay: Overlay,
    private inspectionImageService: InspectionImageService,
    private dialog: MatDialog,
    private oscSpinnerService: SpinnerService,
    private blobService: BlobService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private storageService: StorageService,
    private zone: NgZone
  ) {
    this.langService.prepare(translate, this.onDestroy$, (res) => {
      this.editFunctions[0] = this.translate.instant("imageAnnotationEdit.annotation");
      this.editFunctions[1] = this.translate.instant("imageAnnotationEdit.imageRetouch");
    });
    this.selectFunction = this.editFunctions[0];

    // Session Storageに設定情報が保存されている場合、"Object Style"に反映
    const param = this.storageService.fetchRetouchSettingQueryParam(INSPECTION_IMAGE__RETOUCH_SETTING_QUERY_PARAMS);
    if (param) {
      this.isFilled = param.fill.isFill;
      this.fillColor = param.fill.color;
      this.borderType = param.border.strokeWidth;
      this.borderColor = param.border.color;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isDataLoaded && this.isDataLoaded) {
      if (this.inspectionImage && !this.initLoaded) {
        this.initLoaded = true;
        this.oscSpinnerService.show(true);
        this.loadImage();
      }
    }
  }

  ngOnInit(): void {
    this.mainContent = document.getElementById("annotation-main-content");
    this.addResizeEvent();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    clearTimeout(this.resizeTimer);
    window.removeEventListener("resize", this.resizeWindowEvent);

    // "Object Style"情報をSession Storageに保存
    this.storageService.pushRetouchSettingQueryParam(INSPECTION_IMAGE__RETOUCH_SETTING_QUERY_PARAMS, this.RETOUCH_SETTINGS_QUERY_PARAM);
  }

  changeMode(mode: string) {
    if (mode == this.editFunctions[1]) {
      this.canvas.isDrawingMode = false;
      this.canvas.discardActiveObject();
      this.canvas.renderAll();
    }

    this.selectFunction = mode;
    this.cd.detectChanges();
  }

  async loadImage(): Promise<void> {
    // 画像URLを取得
    const imageType = this.inspectionImage.hasMeasurementImage ? USE_IMAGE_TYPE.REMEASUREMENT : USE_IMAGE_TYPE.ORIGINAL;
    this.imageSrc = await this.inspectionImageService.getImageBinaryUrl(this.inspectionImage.inspectionId, this.inspectionImage.id, imageType);

    // URLから画像をbase64形式で読み取る
    await this.blobService
      .getBase64ImageFromUrl(this.imageSrc)
      .then((base64) => {
        const image = new Image();
        this.imgSrcBase64 = base64;
        image.src = base64;
        image.onload = () => this.setupCropCanvas(image);
      })
      .catch((err) => {
        this.oscSpinnerService.hide(true);
        console.error(err);
      });
  }

  /** canvasの設定
   * @returns void
   */
  setupCanvas(image: HTMLImageElement): void {
    this.scale = this.getScale();

    const canvasConfig: Partial<CanvasOptions> = {
      width: image.width * this.scale,
      height: image.height * this.scale,
      centeredScaling: true
    };

    const fabricImage = new FabricImage(image, {
      scaleX: this.scale,
      scaleY: this.scale,
      selectable: false,
      evented: false,
      hasControls: false,
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true
    });

    this.canvasImage = fabricImage;

    // 操作・表示用Canvasの設定
    this.canvas = new Canvas("canvas", canvasConfig);
    this.canvas.add(fabricImage);
    this.canvas.preserveObjectStacking = true;

    // Initialize free drawing brush for Fabric v6
    this.canvas.freeDrawingBrush = new PencilBrush(this.canvas);
    this.canvas.freeDrawingBrush.width = this.borderType;
    this.canvas.freeDrawingBrush.color = this.borderColor;
    this.canvas.hoverCursor = "default";
    this.canvas.isDrawingMode = false;
    this.setupExistingAnnotations();

    // setup initial state.
    this.setCanvasEvent();
    this.isCanvasLoading = false;
    this.emitModifiedData();
    this.addHistory();
    this.initDataJson = this.canvas.toJSON();
    this.oscSpinnerService.hide(true);
  }

  private setupExistingAnnotations() {
    if (this.annotationObjects?.modifiedItems) {
      const existingAnnotations = this.annotationObjects.modifiedItems.filter((item) => item.type.toLowerCase() !== "image");
      const originalScale = this.annotationObjects.modifiedItems.filter((item) => item.type.toLowerCase() === "image")[0].scaleX;
      this.generateCanvasObjects(existingAnnotations, originalScale);
    }
  }

  setCanvasEvent() {
    // this.canvas.on returns a disposer for the event. It can later be used to unsubscribe to it.
    this.canvasEventDisposers = [
      this.canvas.on("object:added", (e) => this.changedObjects()),
      this.canvas.on("object:modified", (e) => this.changedObjects()),
      this.canvas.on("selection:cleared", (e) => (this.firstActiveObjIndex = -1))
    ];
  }

  removeCanvasEvent() {
    // unsubscribe to each event using disposer.
    this.canvasEventDisposers.forEach((dispose) => dispose());
    this.canvasEventDisposers = [];
  }

  /** canvasに四角形オブジェクトを配置する */
  public addRect(): void {
    if (this.isCanvasLoading) {
      return;
    }

    const obj = this.generateRect(
      100 * this.scale,
      100 * this.scale,
      100 * this.scale,
      100 * this.scale,
      this.getFillColor(),
      this.borderType,
      this.borderColor
    );
    this.canvas.add(obj);

    const objects = this.canvas.getObjects();
    const activateObj = objects.find((o, i) => i === objects.length - 1);
    this.canvas.setActiveObject(activateObj);
    this.canvas.isDrawingMode = false;
    this.canvas.renderAll();
  }

  /** canvasに円形オブジェクトを配置する */
  public addCircle(): void {
    if (this.isCanvasLoading) {
      return;
    }

    const obj = this.generateCircle(
      100 * this.scale,
      100 * this.scale,
      50 * this.scale,
      50 * this.scale,
      this.getFillColor(),
      this.borderType,
      this.borderColor
    );
    this.canvas.add(obj);

    const objects = this.canvas.getObjects();
    const activateObj = objects.find((o, i) => i === objects.length - 1);
    this.canvas.setActiveObject(activateObj);
    this.canvas.isDrawingMode = false;
    this.canvas.renderAll();
  }

  public addText(): void {
    if (this.isCanvasLoading) {
      return;
    }

    const dialogRef = this.dialog.open(AddTextComponent, {
      width: "450px",
      disableClose: true
    });

    dialogRef.componentInstance.addEmitter.subscribe((text) => {
      const obj = this.generateText(100 * this.scale, 100 * this.scale, 50 * this.scale, text, this.borderColor, 100 * this.scale);
      this.canvas.add(obj);

      const objects = this.canvas.getObjects();
      const activateObj = objects.find((o, i) => i === objects.length - 1);
      this.canvas.setActiveObject(activateObj);
      this.canvas.isDrawingMode = false;
      this.canvas.renderAll();
      dialogRef.close();
    });
  }

  public addLine(): void {
    if (this.isCanvasLoading) {
      return;
    }

    const obj = this.generateLine(100 * this.scale, 100 * this.scale, 280 * this.scale, 100 * this.scale, 6 * this.scale, this.borderColor);
    this.canvas.add(obj);

    const objects = this.canvas.getObjects();
    const activateObj = objects.find((o, i) => i === objects.length - 1);
    this.canvas.setActiveObject(activateObj);
    this.canvas.isDrawingMode = false;
    this.canvas.renderAll();
  }

  // 矢印を追加
  public addAllow(): void {
    if (this.isCanvasLoading) {
      return;
    }

    const obj = this.generateText(100 * this.scale, 100 * this.scale, 50 * this.scale, "→", this.borderColor, 100 * this.scale);
    this.canvas.add(obj);

    const objects = this.canvas.getObjects();
    const activateObj = objects.find((o, i) => i === objects.length - 1);
    this.canvas.setActiveObject(activateObj);
    this.canvas.isDrawingMode = false;
    this.canvas.renderAll();
  }

  public toggleFreeHand(isFreehand: boolean): void {
    this.changeMode(this.editFunctions[0]);

    if (this.isCanvasLoading) {
      return;
    }

    this.canvas.isDrawingMode = isFreehand;
  }

  public changeLayer(isUp: boolean): void {
    const activeObjects = this.canvas.getActiveObjects();

    if (isUp) {
      // 上位位レイヤーから処理する
      activeObjects.reverse().forEach((obj) => {
        const zIndex = this.canvas._objects.indexOf(obj);
        if (zIndex > 0) {
          this.canvas.moveObjectTo(obj, zIndex + 1);
        }
      });
    } else {
      // 下位レイヤーから処理する
      activeObjects.forEach((obj) => {
        const zIndex = this.canvas._objects.indexOf(obj);
        if (zIndex > 1) {
          this.canvas.moveObjectTo(obj, zIndex - 1);
        }
      });
    }

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeLayerToTop(): void {
    const activeObjects = this.canvas.getActiveObjects();

    // 下位レイヤーから処理する
    activeObjects.forEach((obj) => {
      const zIndex = this.canvas._objects.indexOf(obj);
      if (zIndex > 0) {
        this.canvas.moveObjectTo(obj, this.canvas._objects.length - 1);
      }
    });

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeLayerToBottom(): void {
    const activeObjects = this.canvas.getActiveObjects();

    // 上位位レイヤーから処理する
    activeObjects.reverse().forEach((obj) => {
      const zIndex = this.canvas._objects.indexOf(obj);
      if (zIndex > 0) {
        this.canvas.moveObjectTo(obj, 1);
      }
    });

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public removeObject(): void {
    const activeObjects = this.canvas.getActiveObjects();
    activeObjects.forEach((obj) => {
      if (!obj.isType("image")) {
        this.canvas.remove(obj);
      }
    });

    this.canvas.renderAll();
    this.syncCanvasData(false);
    this.firstActiveObjIndex = -1;
  }

  public isActiveObject(): boolean {
    return this.canvas && this.canvas._objects && this.canvas._objects.length > 1 && this.canvas.getActiveObjects().length > 0;
  }

  public changeFillColor(): void {
    this.canvas.getActiveObjects().forEach((obj) => {
      if (obj.isType("rect", "ellipse")) {
        obj.set({ fill: this.getFillColor() });
      }
    });

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeBorderColor(): void {
    this.canvas.getActiveObjects().forEach((obj) => {
      if (obj.isType("text")) {
        obj.set({ fill: this.borderColor });
      } else {
        obj.set({ stroke: this.borderColor });
      }
    });
    this.canvas.freeDrawingBrush.color = this.borderColor;

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeBorderType() {
    this.canvas.getActiveObjects().forEach((obj) => {
      if (obj.isType("text")) {
        (obj as FabricText).set({ fontSize: this.borderType });
      } else {
        obj.set({ strokeWidth: this.borderType });
      }
    });

    const width = this.borderType >= 1 ? this.borderType : 1;
    this.canvas.freeDrawingBrush.width = width;

    this.canvas.renderAll();
    this.syncCanvasData(false);
  }

  public changeBrightness(brightness: number, isDetermined = false) {
    this.brightness = brightness;
    this.canvas.getObjects().forEach((obj) => {
      if (obj.isType("image")) {
        this.setBrightness(obj as FabricImage, this.brightness);
      }
    });
    this.canvas.requestRenderAll();

    if (isDetermined) {
      this.syncCanvasData(false);
    }
  }

  public async rotate(angle: number, data: any, isFirstLoad: boolean) {
    const mainObjects = this.canvas.getObjects();

    if (mainObjects.length > 1 && isFirstLoad) {
      // if annotations exist, confirm whether to delete the annotations
      const dialogRef = this.openFlushConfirmDialog();
      const pressOK = await dialogRef
        .afterClosed()
        .toPromise()
        .then((result) => result);

      if (!pressOK) {
        return;
      }

      this.removeAllObjectsFromCanvas();
    }

    const mainImage = this.canvas.getObjects()[0];

    const sumAngle = mainImage.angle + angle;
    const newAngle = Math.abs(sumAngle % 360) ? sumAngle : 0;

    mainImage.rotate(newAngle);

    this.canvas.renderAll();
    if (data) {
      this.addAnnotation(data);
    } else {
      this.addHistory();
    }
    this.emitModifiedData();
  }

  public addHistory() {
    // 通常の場合
    if (this.activeHistoryIndex < this.MAX_HISTORIES) {
      this.activeHistoryIndex++;
      // splice 第一引数から第二引数文削除し、第三引数以降を挿入する
      this.histories = this.histories.filter((v, i) => i < this.activeHistoryIndex);
      this.histories.push(this.canvas.toJSON());
    } else {
      // 最大数持っている場合
      this.histories.shift();
      this.histories.push(this.canvas.toJSON());
    }
  }

  public async undo() {
    if (!this.isCanvasLoading && this.activeHistoryIndex > 0) {
      this.activeHistoryIndex--;
      await this.applyCurrentActiveHistoryState();
    }
  }

  public async redo() {
    if (!this.isCanvasLoading && this.activeHistoryIndex < this.histories.length - 1) {
      this.activeHistoryIndex++;
      await this.applyCurrentActiveHistoryState();
    }
  }

  private removeAllObjectsFromCanvas() {
    const objectsToRemove = this.canvas.getObjects().filter((obj) => !obj.isType("image"));
    objectsToRemove.forEach((obj) => this.canvas.remove(obj));
  }

  private async applyCurrentActiveHistoryState() {
    this.removeCanvasEvent(); // Disable events to bypass history.
    this.removeAllObjectsFromCanvas();
    await this.canvas.loadFromJSON(this.histories[this.activeHistoryIndex]);
    this.canvas.renderAll();
    this.setCanvasEvent(); // reenable events
  }

  public getColorName(colorCode: string): string {
    const color = this.selectColors.find((_) => _.color === colorCode);
    return color ? this.translate.instant("imageAnnotationEdit.annotation_color_" + color.name.toLowerCase()) : "";
  }

  public formatBrightnessLabel(value: number) {
    return Math.floor(value * 100);
  }
  public async showTrimCanvas(): Promise<void> {
    const objects = this.canvas.getObjects();
    let executeFlush = false;
    if (objects.length > 1) {
      // If annotations exist, confirm whether to delete the annotations
      const flushDialogRef = this.openFlushConfirmDialog();
      executeFlush = await flushDialogRef
        .afterClosed()
        .toPromise()
        .then((result) => result);
    }

    const trimDialogRef = this.openImageTrimDialog();
    trimDialogRef.componentInstance.closePanel.pipe(takeUntil(this.onDestroy$)).subscribe((info) => {
      if (executeFlush) {
        // 画像以外削除
        this.canvas._objects = objects.filter((o, i) => i === 0);
        this.canvas.discardActiveObject();
      }

      if (info) {
        this.updateMainImageSrc(info[0], info[1], 0, false, false);
        this.rectangle = info[1];
      }

      trimDialogRef.close();
    });
  }

  public async revertToOriginal(): Promise<void> {
    const objects = this.canvas.getObjects();
    if (objects.length > 1) {
      // アノテーションが存在する場合、アノテーション削除確認をする
      const dialogRef = this.openFlushConfirmDialog();
      const pressOK = await dialogRef
        .afterClosed()
        .toPromise()
        .then((result) => result);
      if (!pressOK) {
        return;
      }
    }

    this.removeAllObjectsFromCanvas();

    this.updateMainImageSrc(this.imgSrcBase64, null, 0, false, true);
  }

  /** 四角形オブジェクトを作成する */
  private generateRect(
    left: number,
    top: number,
    width: number,
    height: number,
    color: string,
    strokeWidth: number,
    stroke: string,
    globalCompositeOperation = null
  ): Rect {
    const obj = new Rect({
      left: left,
      top: top,
      width: width,
      height: height,
      fill: color,
      strokeWidth: strokeWidth,
      stroke: stroke,
      globalCompositeOperation: globalCompositeOperation
    });

    return obj;
  }

  private generateCircle(left: number, top: number, rx: number, ry: number, color: string, strokeWidth: number, stroke: string): Ellipse {
    const obj = new Ellipse({
      left: left,
      top: top,
      rx: rx,
      ry: ry,
      fill: color,
      strokeWidth: strokeWidth,
      stroke: stroke
    });

    return obj;
  }

  private generateText(left: number, top: number, width: number, text: string, color: string, fontSize: number): FabricText {
    const obj = new FabricText(text, {
      left: left,
      top: top,
      fill: color,
      fontSize: fontSize,
      width: width
    });
    return obj;
  }

  private generateLine(left: number, top: number, endX: number, endY: number, strokeWidth: number, stroke: string): Line {
    const obj = new Line([left, top, endX, endY], {
      stroke: stroke,
      strokeWidth: strokeWidth
    });

    return obj;
  }

  get OverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      // 表示位置に関する設定
      positionStrategy: this.overlay.position().global().top().left(),
      hasBackdrop: true, // 背景の表示
      backdropClass: ["custom-overlay"], // 背景に指定するクラス
      disposeOnNavigation: true // ナビゲーション時に削除
    });
  }

  get isUndoDisabled() {
    return !this.activeHistoryIndex || this.activeHistoryIndex < 1;
  }

  get isRedoDisabled() {
    return this.activeHistoryIndex >= this.histories.length - 1;
  }

  private emitModifiedData() {
    const canvasJsonObject = this.canvas.toJSON();

    // Compare with initial state
    if (JSON.stringify(canvasJsonObject) !== JSON.stringify(this.initDataJson)) {
      // Update if there are any changes
      const modifiedData = canvasJsonObject;
      modifiedData.objects.some((obj, i) => {
        if (obj.type.toLowerCase() === "image" && i === 0) {
          obj.src = "";
          return true;
        }
        return false;
      });

      if (modifiedData.objects[0] && !modifiedData.objects[0].data) {
        modifiedData.objects[0].data = [null, { isNewSpecification: true }];
      }

      const base64Data = this.canvas.toDataURL({
        format: "jpeg",
        quality: 1,
        multiplier: 1
      });
      const blob = this.toBlob(base64Data);

      const result = new AnnotationEmitData(blob, modifiedData.objects, null, this.initDataJson, false);
      this.modified.emit(result);
    } else {
      // Update the object with null if no changes have been made。
      const result = new AnnotationEmitData(null, null, null, this.initDataJson, false);
      this.modified.emit(result);
    }
  }

  getSelectedIconStyle(isSelected: boolean): object {
    const style = {};
    if (isSelected) {
      style["background-color"] = "#08107b";
      style["opacity"] = 0.6;
      style["border-radius"] = 0;
    }
    return style;
  }

  private changedObjects() {
    this.syncCanvasData(false);
  }

  generateCanvasObjects(objects: any[], originalScale: number | undefined = undefined) {
    const scaleRatio = originalScale ? this.scale / originalScale : 1 / this.scale;
    const scale = 1 / scaleRatio;
    objects.forEach((_) => {
      let obj;
      if (_.type.toLowerCase() === "image") {
        /* empty */
      } else if (_.type.toLowerCase() === "rect") {
        // Rectアノテーションの作成
        obj = this.generateRect(_.left / scale, _.top / scale, _.width / scale, _.height / scale, _.fill, _.strokeWidth / scale, _.stroke);
      } else if (_.type.toLowerCase() === "ellipse") {
        // Circleアノテーションの作成
        obj = this.generateCircle(_.left / scale, _.top / scale, _.rx / scale, _.ry / scale, _.fill, _.strokeWidth / scale, _.stroke);
      } else if (_.type.toLowerCase() === "line") {
        // Lineアノテーションの作成
        obj = this.generateLine(
          _.left / scale,
          _.top / scale,
          (_.left + _.width) / scale,
          (_.top + _.height) / scale,
          _.strokeWidth / scale,
          _.stroke
        );
      } else if (_.type.toLowerCase() === "text" && _.text === "→") {
        // Arrowアノテーションの作成
        obj = this.generateText(_.left / scale, _.top / scale, _.width / scale, "→", _.fill, _.fontSize / scale);
      } else if (_.type.toLowerCase() === "text") {
        // Textアノテーションの作成
        obj = this.generateText(_.left / scale, _.top / scale, _.width / scale, _.text, _.fill, _.fontSize / scale);
      } else if (_.path) {
        // FreeHandアノテーションの作成
        const path = _.path.map((item) => {
          if (item[0] === "M" || item[0] === "L") {
            return [item[0], item[1] / scale, item[2] / scale];
          } else {
            return [item[0], item[1] / scale, item[2] / scale, item[3] / scale, item[4] / scale];
          }
        }) as Point[];
        obj = new Path(path.toString());
        obj.fill = null;
        obj.stroke = _.stroke;
        obj.strokeWidth = _.strokeWidth / scale;
        obj.top = _.top / scale;
        obj.left = _.left / scale;
        obj.width = _.width / scale;
        obj.height = _.height / scale;
        obj.strokeLineCap = _.strokeLineCap;
        obj.strokeLineJoin = _.strokeLineJoin;
        obj.strokeMiterLimit = _.strokeMiterLimit;
      }

      if (obj && _.type.toLowerCase() !== "image") {
        obj.scaleX = _.scaleX;
        obj.scaleY = _.scaleY;
        obj.angle = _.angle;
        this.canvas.add(obj);
      }
    });
    this.canvas.renderAll();
  }

  addAnnotation(data: any) {
    this.canvas._objects = this.canvas._objects.filter((obj) => obj.isType("image"));
    this.generateCanvasObjects(data.objects);
    this.syncCanvasData(false);
    this.emitModifiedData();
  }

  private syncCanvasData(isMain: boolean) {
    if (!isMain) {
      this.addHistory();
      this.isCanvasLoading = false;
      this.emitModifiedData();
    }
  }

  private getFillColor(): string {
    return this.isFilled ? this.fillColor : null;
  }

  private toBlob(base64: string, type: string = "image/jpeg"): Blob {
    // Base64からバイナリへ変換
    const bin = atob(base64.replace(/^.*,/, ""));
    const buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
    }

    // Blobを作成
    return new Blob([buffer.buffer], { type: type });
  }

  private setBrightness(img: FabricImage, brightness: number) {
    const filter = new filters.Brightness({ brightness });
    img.filters = [filter];
    img.applyFilters();
  }

  private openFlushConfirmDialog(): MatDialogRef<CommonConfirmDialogComponent> {
    return this.dialog.open(CommonConfirmDialogComponent, {
      width: "500px",
      height: "230px",
      data: {
        firstMessage: this.translate.instant("imageAnnotationEdit.openFlushConfirmDialogFirstMessage"),
        secondMessage: "",
        dialogType: "confirmation",
        trueBtnText: this.translate.instant("imageAnnotationEdit.openFlushConfirmDialogTrueBtnText")
      }
    });
  }

  private openImageTrimDialog(): MatDialogRef<ImageTrimComponent> {
    return this.dialog.open(ImageTrimComponent, {
      panelClass: "image-trim",
      data: {
        src: this.imgSrcBase64
      }
    });
  }

  private updateMainImageSrc(imgSrc: string, rect: any, angle: number, isFirstLoad: boolean, isRevert: boolean, data: any = null): void {
    this.isCanvasLoading = true;

    const image = new Image();
    image.src = imgSrc;
    this.cropImageSrc = imgSrc;
    image.onload = async () => {
      await this.cropCanvas.dispose();
      await this.canvas.dispose();
      this.setupCropCanvas(image);
    };
  }

  /** 画像がトリムされたものかどうか判別する
   * @param  {fabric.Image} imageObj
   * @returns boolean
   */
  public isTrimmed(imageObj: FabricImage = null): boolean {
    if (!this.canvas || this.canvas.getObjects().length < 1) {
      return false;
    }

    if (!imageObj) {
      imageObj = this.canvas.getObjects().find((obj) => obj.isType("image")) as FabricImage;
    }

    return imageObj.cropX != 0 || imageObj.cropY != 0;
  }

  private setupCropCanvas(image: HTMLImageElement): void {
    this.baseImage = image;
    this.baseImageWidth = image.width;
    this.baseImageHeight = image.height;

    const width = this.TRIM_WIDTH / this.baseImageWidth;
    const height = this.TRIM_HEIGHT / this.baseImageHeight;
    const scale = Math.min(width, height);
    const imgConfig: any = {
      top: this.TRIM_WIDTH / 2,
      left: this.TRIM_HEIGHT / 2,
      originX: "center",
      originY: "center",
      scaleX: scale,
      scaleY: scale
    };

    const cropImage: FabricImage = new FabricImage(image, imgConfig);
    this.cropCanvas = new Canvas("cropCanvas");
    this.cropCanvas.setDimensions({ width: cropImage.width * scale, height: cropImage.height * scale });
    this.cropCanvas.add(cropImage);
    this.cropCanvas.centerObject(cropImage);
    this.cropCanvas.add(this.rectangle);
    this.cropCanvas.renderAll();
    this.setupCanvas(image);
  }

  public crop(angle: number, data: any, isFirstLoad: boolean): any {
    const imageSrc = this.cropCanvas.toDataURL({
      left: this.rectangle.left,
      top: this.rectangle.top,
      width: this.rectangle.width,
      height: this.rectangle.height,
      quality: 1,
      format: "jpeg",
      multiplier: 1
    });

    this.updateMainImageSrc(imageSrc, this.rectangle, angle, isFirstLoad, false, data);
  }

  public addResizeEvent() {
    window.addEventListener("resize", this.resizeWindowEvent);
  }

  private resizeWindowEvent = () => {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }

    this.resizeTimer = setTimeout(() => {
      this.resizeWindow();
    }, 1000);
  };

  public async resizeWindow() {
    // If you don't delete the canvas and then add it again, it won't render correctly.
    await this.canvas.dispose();
    await this.cropCanvas.dispose();
    this.setupCropCanvas(this.baseImage);
  }

  public isVerticalImage(): boolean {
    if (!this.canvas) {
      return;
    }

    return this.canvas.getHeight() >= this.canvas.getWidth();
  }

  public getScale(): number {
    let scale: number;
    if (this.isEditable) {
      const mainContent = document.getElementById("annotation-main-content");
      const functionArea = document.getElementById("function-area");
      const maxImg = Math.min(this.baseImageWidth, this.baseImageHeight);
      const minContent = Math.min(mainContent.clientWidth - functionArea.clientWidth - 60 - 144, mainContent.clientHeight);
      scale = minContent / maxImg;
    } else {
      const container = document.getElementById("annotation-main-content");
      scale = Math.min(container.clientWidth / this.baseImageWidth, container.clientHeight / this.baseImageHeight) * 0.9;
    }
    return scale;
  }

  public getFuncAreaStyle(): object {
    const style = {};
    const mainContent = document.getElementById("annotation-main-content");
    const canvasArea = document.getElementById("canvas-area");
    const margin = (mainContent.clientHeight - canvasArea.clientHeight) / 2;
    return style;
  }

  public getClearAllStyle(): object {
    const style = {};
    const mainContent = document.getElementById("annotation-main-content");
    const canvasArea = document.getElementById("canvas-area");

    const bottom = (mainContent.clientHeight - canvasArea.clientHeight) / 2;
    // 72: Dataタブの矢印スペース分, 100: clearAllButtonの横幅
    const left = mainContent.clientWidth - 72 - 100;

    style["bottom"] = bottom.toString() + "px";
    style["left"] = left.toString() + "px";
    return style;
  }

  // Scrollbarが出た際の調整
  public getObjectsAreaStyle(): { [key: string]: string } {
    if (this.isScroll()) {
      return { width: "44px" };
    } else {
      return {
        "overflow-x": "clip",
        "overflow-y": "auto",
        width: "58px"
      };
    }
  }

  public isScroll(): boolean {
    return 400 <= this.mainContent.clientHeight;
  }

  clearAllModifiedImage(): void {
    const message = this.translate.instant("imageAnnotationEdit.clearModifiedImageFirstMessage", { fileName: this.inspectionImage.fileName });
    const dialogRef = this.dialog.open(CommonConfirmDialogComponent, {
      width: "570px",
      height: "270px",
      data: {
        firstMessage: message,
        secondMessage: "",
        dialogType: "confirmation",
        trueBtnText: "OK"
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (!this.isCanvasLoading && this.activeHistoryIndex > 0) {
          this.activeHistoryIndex = 0;
          this.applyCurrentActiveHistoryState();
        }
      }
    });
  }

  isUneditedRetouchImage(): boolean {
    const annotationObjects = this.canvas?._objects as any;

    if (!annotationObjects) {
      return true;
    }

    return (
      annotationObjects.length === 1 &&
      annotationObjects[0].type.toLowerCase() === "image" &&
      annotationObjects[0].filters[0].brightness === 0 &&
      annotationObjects[0].angle === 0 &&
      annotationObjects[0].width === this.inspectionImage.originalWidth &&
      annotationObjects[0].height === this.inspectionImage.originalHeight
    );
  }
}
